import React from 'react'
import InputLabel from '@mui/material/InputLabel'
import MenuItem from '@mui/material/MenuItem'
import FormControl from '@mui/material/FormControl'
import FormHelperText from '@mui/material/FormHelperText'
import Select from '@mui/material/Select'
import PropTypes from 'prop-types'

/**
 * A DOM id from a visible label. The raw label was used before, so "Event
 * Type" produced aria-labelledby="Event Type-select-label", which a browser
 * reads as two ids that do not exist: every multi-word select had no
 * accessible name.
 */
export const selectId = (label) =>
    `select-${String(label || 'field')
        .toLowerCase()
        .replace(/[^a-z0-9]+/g, '-')
        .replace(/^-|-$/g, '')}`

// Dark styling comes from the theme; no per-component overrides needed.
const SingleSelect = ({
    label,
    value,
    optionValueText,
    optionLabelText,
    options = [],
    handleChange,
    error,
    ariaLabel,
    disabled = false,
    helperText,
    size = 'small',
    required = false,
}) => (
    <FormControl
        fullWidth
        size={size}
        error={error}
        disabled={disabled}
        // The helper text overlays below the field instead of adding height, so
        // it cannot push the select up or move it as the text comes and goes.
        sx={helperText ? { position: 'relative' } : undefined}
    >
        <InputLabel required={required} id={`${selectId(label)}-label`}>
            {label}
        </InputLabel>
        <Select
            labelId={`${selectId(label)}-label`}
            id={selectId(label)}
            value={value ?? ''}
            label={required ? `${label} *` : label}
            onChange={(e) => handleChange(e.target.value)}
            // Callers that render their own visible label pass label=""; the
            // select then needs a name of its own for screen readers.
            {...(ariaLabel
                ? {
                      inputProps: { 'aria-label': ariaLabel },
                      SelectDisplayProps: { 'aria-label': ariaLabel },
                  }
                : {})}
            fullWidth
        >
            {options.map((option) => (
                <MenuItem
                    key={option[optionValueText]}
                    value={option[optionValueText]}
                >
                    {option[optionLabelText]}
                </MenuItem>
            ))}
        </Select>
        {helperText && (
            <FormHelperText
                sx={{
                    position: 'absolute',
                    top: '100%',
                    left: 0,
                    right: 0,
                    // The same inset as a text field's helper line.
                    mx: '14px',
                    mt: '3px',
                    mb: 0,
                    lineHeight: 1.2,
                    // A disabled select dims its note; keep the same colour as a
                    // text field's helper line (an error still shows red).
                    color: 'text.secondary',
                    '&.Mui-disabled': { color: 'text.secondary' },
                }}
            >
                {helperText}
            </FormHelperText>
        )}
    </FormControl>
)

SingleSelect.propTypes = {
    label: PropTypes.string.isRequired,
    value: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    optionValueText: PropTypes.string,
    optionLabelText: PropTypes.string,
    options: PropTypes.arrayOf(
        PropTypes.shape({
            label: PropTypes.string,
            value: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
        })
    ),
    handleChange: PropTypes.func,
    error: PropTypes.bool,
    ariaLabel: PropTypes.string,
    size: PropTypes.oneOf(['small', 'medium']),
    // Marks the label with an asterisk, as a required text field does.
    required: PropTypes.bool,
    // Shown but not changeable (the channel of a template being edited).
    disabled: PropTypes.bool,
    helperText: PropTypes.string,
}

export default SingleSelect
